import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Logo } from "./top-bar/Logo";
import { Navigation } from "./top-bar/Navigation";
import { UserMenu } from "./top-bar/UserMenu";
import { Menu } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const TopBar = () => {
  const [user, setUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Check if we're in the guest layout
  const isGuestLayout = location.pathname.startsWith('/guest');

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          return;
        }

        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchUserRole(session.user.id);
        }

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Auth state changed:', event);
            
            if (event === 'SIGNED_OUT') {
              setUser(null);
              setUserRole(null);
              navigate('/login');
              return;
            }

            if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
              setUser(session.user);
              await fetchUserRole(session.user.id);
            }
          }
        );

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setUserRole(null);
      }
    };

    initializeAuth();
  }, [navigate]);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error fetching user role:", error);
        return;
      }

      setUserRole(data.role);
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  const handleLogout = async () => {
    try {
      // Clear local state first
      setUser(null);
      setUserRole(null);
      
      // Clear any stored return paths
      localStorage.removeItem('returnTo');
      
      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Logout error:", error);
        // Even if there's an error, we'll show a success message and redirect
        // This ensures the user experience remains smooth
        toast({
          title: "Signed out",
          description: "You have been signed out.",
        });
      } else {
        toast({
          title: "Signed out successfully",
          description: "You have been logged out.",
        });
      }
      
      // Always navigate to home page
      navigate("/");
      
    } catch (error) {
      console.error("Logout error:", error);
      // Even if there's an error, we'll redirect the user
      toast({
        title: "Signed out",
        description: "You have been signed out.",
      });
      navigate("/");
    }
  };

  const getDashboardLink = () => {
    switch (userRole) {
      case "admin":
        return "/admin";
      case "host":
        return "/host";
      case "guest":
        return "/guest";
      default:
        return "/";
    }
  };

  return (
    <div className="border-b bg-white">
      <div className="flex justify-between items-center h-16 px-4">
        <div className="flex items-center space-x-4">
          <Logo />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-4">
          <Navigation user={user} userRole={userRole} />
          <UserMenu 
            user={user}
            getDashboardLink={getDashboardLink}
            handleLogout={handleLogout}
          />
        </div>

        {/* Mobile Navigation - Only show if not in guest layout */}
        {!isGuestLayout && (
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[80%] sm:w-[385px]">
                <div className="flex flex-col space-y-4 py-4">
                  <Navigation user={user} userRole={userRole} />
                  <div className="pt-4 border-t">
                    <UserMenu 
                      user={user}
                      getDashboardLink={getDashboardLink}
                      handleLogout={handleLogout}
                    />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopBar;
